/**
 * BYO Flow core model.
 *
 * Pure, framework-free types and helpers describing a "Bring Your Own" (BYO)
 * flow. The backend `flow_type` for BYO is `custom` (see
 * {@link file://src/new/utils/flowTypeMapping.ts}); the UI/route name is `byo`.
 *
 * This module is intentionally React-free and 100% unit-testable. UI-shell
 * prototypes compose these types and call the pure helpers.
 */

import { get, set } from 'lodash';
import { toBackendFlowType, toUIFlowType } from 'new/utils/flowTypeMapping';
import {
  DEFAULT_CODE,
  DEFAULT_CODE_FLEX_FLOW,
  // Direct module path, not the barrel: the barrel re-exports LoadCustomProcessor,
  // which imports parsePackages from this file — going through it would create
  // a model -> barrel -> LoadCustomProcessor -> model import cycle.
} from 'new/components/flows/shared/customProcessor/CustomProcessorConstants';

// ---------------------------------------------------------------------------
// Re-exported default code templates (single source of truth lives in
// CustomProcessorConstants). Shells should import these from here so the BYO
// core remains the only surface they depend on.
// ---------------------------------------------------------------------------
export { DEFAULT_CODE, DEFAULT_CODE_FLEX_FLOW };

/** How the source data should be parsed before reaching user code. */
export type ByoParseMode = 'raw' | 'parsed';

/** Code authoring mode: write inline custom code or reuse a saved processor. */
export type ByoCodeMode = 'predefined' | 'custom';

/**
 * Source half of a BYO flow: where the data comes from and how it is read.
 * `connectionId`/`credentialId` are numeric resource ids in the backend but are
 * modelled loosely here so drafts can hold partial values.
 */
export interface ByoSourceValue {
  connectionId?: number | string | null;
  credentialId?: number | string | null;
  /** Selected path / bucket / table identifier. */
  path?: string | null;
  parseMode: ByoParseMode;
}

/**
 * Code half of a BYO flow.
 *
 * - `mode === 'custom'`: `code` holds the inline Python source.
 * - `mode === 'predefined'`: `predefinedId` points at a saved code container.
 *
 * `packages` is the normalized list of `name==version` requirement strings.
 * `extraData` mirrors the backend `code_config.extra_data` free-form string.
 */
export interface ByoCodeValue {
  mode: ByoCodeMode;
  code: string;
  predefinedId?: number | string | null;
  packages: string[];
  extraData: string;
}

/** Destination half of a BYO flow. */
export interface ByoDestinationValue {
  connectionId?: number | string | null;
  credentialId?: number | string | null;
  /** Target path / table / topic the data is written to. */
  target?: string | null;
  /** Sink mapping object (shape owned by createFlowService). */
  mapping?: Record<string, unknown> | null;
}

/** A full, in-progress BYO flow composed of its three halves. */
export interface ByoFlowDraft {
  source: ByoSourceValue;
  code: ByoCodeValue;
  destination: ByoDestinationValue;
  /** Optional runtime/capsule cluster id. `null` => Nexla Cloud (default). */
  clusterId?: number | string | null;
}

// ---------------------------------------------------------------------------
// Package list (de)serialization.
//
// Convention: COMMA-separated, one `name==version` requirement per item.
// Confirmed 2026-08-19 against the real backend consumer of `code_config.packages`
// (backend-connectors/probe-http RayDryRunner.scala:232, `packages.split(",")`) —
// the field is a plain string split on comma, not newline. See
// docs/byo/byo-v2-review-2026-08-19.md for the trace. Splitting is bracket-depth
// aware so pip "extras" syntax (`pkg[extra1,extra2]==1.0`) isn't corrupted by a
// comma that belongs *inside* the requirement rather than between requirements.
// ---------------------------------------------------------------------------

/**
 * Split a raw requirements string into requirement tokens on commas and
 * newlines, without splitting inside `[...]` (pip extras, e.g.
 * `requests[security,socks]==2.28.0`).
 */
function splitPackageTokens(raw: string): string[] {
  const tokens: string[] = [];
  let current = '';
  let bracketDepth = 0;
  for (const ch of raw) {
    if (ch === '[') bracketDepth += 1;
    else if (ch === ']') bracketDepth = Math.max(0, bracketDepth - 1);

    if (bracketDepth === 0 && (ch === ',' || ch === '\n' || ch === '\r')) {
      tokens.push(current);
      current = '';
    } else if (ch !== '\r') {
      current += ch;
    }
  }
  tokens.push(current);
  return tokens;
}

/**
 * Parse a raw requirements string (comma- and/or newline-separated — tolerant
 * of both so previously-persisted, newline-joined containers still hydrate
 * correctly) into a normalized list.
 * - Trims each token.
 * - Drops blank tokens.
 * - De-duplicates (case-sensitive, first occurrence wins).
 */
export function parsePackages(raw: string): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const token of splitPackageTokens(raw)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

/**
 * Serialize a package list back into the comma-separated string the backend
 * expects (see module doc above). Applies the same trim/dedupe/drop-blank
 * normalization as {@link parsePackages} so round-tripping is stable.
 *
 * Note: a requirement whose own extras contain a comma (e.g.
 * `pkg[extra1,extra2]`) is preserved correctly here, but the backend's own
 * parser is a naive comma-split with no bracket awareness — combining such a
 * requirement with any *other* requirement in the same string will still be
 * misread server-side. There is no purely frontend fix for that; surface a
 * warning to the user instead (see PackagesTable) rather than silently
 * shipping a value the backend cannot parse.
 */
export function serializePackages(list: string[]): string {
  if (!list || list.length === 0) return '';
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of list) {
    const trimmed = (item ?? '').trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result.join(',');
}

// ---------------------------------------------------------------------------
// Flow type helpers
// ---------------------------------------------------------------------------

/** Backend `flow_type` value for BYO flows. */
export const BYO_BACKEND_FLOW_TYPE = 'custom';
/** UI/route name for BYO flows. */
export const BYO_UI_FLOW_TYPE = 'byo';

/**
 * `source_config['custom_runtime.mode']` value BYO always needs. Read directly
 * by `probe-http`'s `ProbeApiHandler.scala` (file-preview/sampling — the
 * "Choose Source Folder" file listing and File Preview panel) to decide
 * whether listed files are treated as raw (REPLICATION) or parsed (PARSING).
 * BYO never wants server-side parsing — the user's own code reads the raw
 * file — so this is always REPLICATION, independent of the separate
 * IMC/Ray job-submission path (which genuinely doesn't read this field).
 * Confirmed by tracing `ProbeApiHandler.scala:1356-1374`: when absent, it
 * falls back to `PARSING` unless `source_config['advanced_settings']` is set
 * to `"Raw File Data"` — which BYO also never sets — so omitting this field
 * silently mis-classifies BYO file sampling as PARSING.
 */
export const BYO_CUSTOM_RUNTIME_MODE = 'REPLICATION';

/** `sourceConfig` with the `custom_runtime.mode` every BYO save and test probe needs. */
export function withByoRuntimeMode<T extends object>(
  sourceConfig: T,
): T & { 'custom_runtime.mode': string } {
  return { ...sourceConfig, 'custom_runtime.mode': BYO_CUSTOM_RUNTIME_MODE };
}

/**
 * True when the given flow type identifies a BYO flow, accepting either the
 * backend (`custom`) or UI (`byo`) spelling.
 */
export function isByoFlowType(flowType: string | null | undefined): boolean {
  if (!flowType) return false;
  return (
    flowType === BYO_BACKEND_FLOW_TYPE ||
    flowType === BYO_UI_FLOW_TYPE ||
    toUIFlowType(flowType) === BYO_UI_FLOW_TYPE ||
    toBackendFlowType(flowType) === BYO_BACKEND_FLOW_TYPE
  );
}

/**
 * Build an empty BYO draft with sensible defaults. Useful for shells starting a
 * brand-new flow.
 */
export function createEmptyByoDraft(): ByoFlowDraft {
  return {
    source: { parseMode: 'raw' },
    code: { mode: 'custom', code: DEFAULT_CODE, packages: [], extraData: '' },
    destination: {},
    clusterId: null,
  };
}

// ── Runtime variables (code container extra_data) ─────────────────────────
// Moved from v2-shells/studio/useStudioDraft.ts: these define the
// job_spec.payload.custom_code.kwargs wire shape, a domain rule shared by the
// v2 modals and both shells, so the shared layer must not import from a shell.

/** extra_data path of the runtime-variable kwargs nexla-ray passes to `process(**kwargs)`. */
export const RUNTIME_VAR_KWARGS_PATH: readonly string[] = [
  'job_spec',
  'payload',
  'custom_code',
  'kwargs',
];
/** Pre-NEX-22815 camelCase path nexla-ray ignores; still read, and removed on save. */
export const LEGACY_RUNTIME_VAR_KWARGS_PATH: readonly string[] = [
  'job_spec',
  'payload',
  'customCode',
  'kwargs',
];

export type RuntimeVarType = 'String' | 'Number' | 'Secret';
export interface RuntimeVar {
  key: string;
  type: RuntimeVarType;
  value: string;
}

/**
 * True when a `Number`-typed row can't serialize to an actual number. Shared
 * by {@link RuntimeVariablesPanel} (field-level error display) and
 * {@link StudioCodeStep} (gates Run on the Test panel) so both agree on what
 * "invalid" means. An *empty* value counts as invalid too — `Number('')` is
 * `0`, not `NaN`, so it previously passed silently and the user's code
 * received `0` for a variable they never actually gave a value, with zero
 * indication anything was wrong. A fully blank row is not a real variable
 * yet, matching `serializeRuntimeVars`'s own `key.trim()` filter — but that
 * filter silently drops a row that has a value and no key, and duplicate keys
 * collapse to one kwarg, so both are invalid ({@link runtimeVarKeyError}).
 *
 * Duplicate detection needs the whole list: pass it directly as the
 * predicate (`vars.filter(isRuntimeVarInvalid)` / `.some(...)`), which
 * supplies it as the third argument.
 */
export function isRuntimeVarInvalid(
  row: RuntimeVar,
  _index?: number,
  rows: readonly RuntimeVar[] = [row],
): boolean {
  if (runtimeVarKeyError(row, rows)) return true;
  if (!row.key.trim() || row.type !== 'Number') return false;
  return row.value.trim() === '' || !Number.isFinite(Number(row.value));
}

/** Why a row's key can't be saved as-is, or null. */
export function runtimeVarKeyError(row: RuntimeVar, rows: readonly RuntimeVar[]): string | null {
  const key = row.key.trim();
  if (!key) return row.value.trim() ? 'Key required' : null;
  return rows.filter((r) => r.key.trim() === key).length > 1 ? 'Duplicate key' : null;
}

/**
 * Serialize runtime variables into the code container's extra_data string.
 *
 * CORRECTED 2026-08-19: previously wrote flat top-level keys
 * (`{"KEY": {type, value}}`), which never reached the user's code — traced
 * the real backend chain (RayApiCodecsV2's `extra_data` deep-merge →
 * nexla-ray's `code_executor.py`, a straight `**kwargs` pass-through with NO
 * unwrapping) and confirmed a value only reaches `kwargs['KEY']` when nested
 * as `job_spec.payload.custom_code.kwargs.KEY`, holding the raw value itself
 * (not a `{type,value}` envelope — the backend never unwraps one). See
 * docs/byo/byo-v2-review-2026-08-19.md §2.
 *
 * NEX-22815: the key is snake_case `custom_code` — nexla-ray's BYO/dry-run
 * models have no `customCode` alias, so the camelCase key this used to write
 * was silently dropped and `process()` only ever got `workspace_dir`.
 *
 * The `type` selector (String/Number/Secret) still matters for the UI — a
 * `Number` var is coerced to an actual JSON number so `kwargs['KEY']` is a
 * real number in Python, not a numeric string. Since the wire format carries
 * no type metadata for round-tripping, `Secret`/`Number` keys are additionally
 * recorded in a small `ui_runtime_var_secret_keys` sidecar (a harmless extra
 * top-level field the backend's request model ignores, the same way it
 * already tolerates unknown top-level keys) purely so the editor can restore
 * the right input type on next edit.
 */
export function serializeRuntimeVars(vars: RuntimeVar[]): string {
  const clean = (vars ?? []).filter((v) => v.key.trim());
  if (!clean.length) return '';
  const kwargs: Record<string, string | number> = {};
  const secretKeys: string[] = [];
  for (const v of clean) {
    const key = v.key.trim();
    // Number('') is 0 (not NaN) and Number('abc') is NaN, which
    // JSON.stringify silently turns into `null` — the pipeline's Python code
    // then received `None` with zero indication why. RuntimeVariablesPanel
    // now blocks most invalid entry at the field, but fall back to the raw
    // string here too rather than ever emitting a silent null. Confirmed
    // bug, fixed 2026-08-20.
    if (v.type === 'Number') {
      const num = Number(v.value);
      kwargs[key] = Number.isFinite(num) ? num : v.value;
    } else {
      kwargs[key] = v.value;
    }
    if (v.type === 'Secret') secretKeys.push(key);
  }
  const payload: Record<string, unknown> = {};
  set(payload, RUNTIME_VAR_KWARGS_PATH, kwargs);
  if (secretKeys.length) payload.ui_runtime_var_secret_keys = secretKeys;
  return JSON.stringify(payload);
}

/** Parse a code container's extra_data JSON; `{}` for empty, non-JSON or non-object input. */
export function parseExtraData(extraData: unknown): Record<string, any> {
  if (!extraData || typeof extraData !== 'string') return {};
  try {
    const parsed = JSON.parse(extraData);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Parse the code container's extra_data string back into runtime variables.
 * Reads `job_spec.payload.custom_code.kwargs` (see {@link serializeRuntimeVars}
 * for why), falling back to the pre-NEX-22815 `customCode` key so flows saved
 * with it still show their variables (the next save moves them); when both
 * are present, `custom_code` wins. Restores `Secret`/`Number` typing from the
 * sidecar / JSON number type respectively; anything else displays as
 * `String`. Ignores the sibling `runtime_config` key — that's the performance
 * config fragment mergeExtraData (services.ts) stuffs into the same
 * extra_data JSON, not a user-authored variable.
 */
export function deserializeRuntimeVars(extraData: unknown): RuntimeVar[] {
  const obj = parseExtraData(extraData);
  const current = get(obj, RUNTIME_VAR_KWARGS_PATH);
  const kwargs =
    current && typeof current === 'object' ? current : get(obj, LEGACY_RUNTIME_VAR_KWARGS_PATH);
  if (!kwargs || typeof kwargs !== 'object') return [];
  const secretKeys = new Set<string>(
    Array.isArray(obj.ui_runtime_var_secret_keys) ? obj.ui_runtime_var_secret_keys : [],
  );
  return Object.entries(kwargs).map(([key, value]: [string, any]) => ({
    key,
    type: secretKeys.has(key) ? 'Secret' : typeof value === 'number' ? 'Number' : 'String',
    value: String(value ?? ''),
  }));
}

// ── Performance config (code container extra_data.runtime_config) ─────────
// Moved from v2-shells/studio/PerformanceConfigPanel.tsx so the shared v2
// layer (edit modal hydration + save) doesn't import from a shell.

export interface BYOPerformanceConfig {
  /** vCPU per file-processing worker (Ray: task num_cpus). */
  cpu: number;
  /** Memory in GB per worker (Ray: task memory). */
  memory: number;
  /** Seconds the job waits idle before shutting down (Ray: max_wait_seconds). */
  timeout: number;
  /** Times a failed file is re-queued before permanent failure (Ray: max_item_requeues). */
  retries: number;
  /** Max files processed in parallel (Ray: runtime_config.max_concurrency). Plan-controlled. */
  concurrency: number;
}

/** Ray's `task_memory_bytes` per GB of the UI's `memory`. */
export const BYTES_PER_GB = 1024 * 1024 * 1024;

export const BYO_PERF_DEFAULTS: BYOPerformanceConfig = {
  cpu: 2,
  memory: 2,
  timeout: 300,
  retries: 3,
  concurrency: 4,
};

/**
 * Read a saved performance config back out of a code container's extra_data
 * string (the inverse of the `runtime_config` fragment mergeExtraData in
 * services.ts writes). Missing knobs fall back to {@link BYO_PERF_DEFAULTS}.
 * Returns `null` when there is no `runtime_config` at all, or extra_data
 * isn't valid JSON — callers keep their current value in that case.
 */
export function deserializePerformanceConfig(extraData: unknown): BYOPerformanceConfig | null {
  const rc = parseExtraData(extraData).runtime_config;
  if (!rc) return null;
  return {
    cpu: rc.task_num_cpus ?? BYO_PERF_DEFAULTS.cpu,
    memory:
      rc.task_memory_bytes != null ? rc.task_memory_bytes / BYTES_PER_GB : BYO_PERF_DEFAULTS.memory,
    timeout: rc.max_wait_seconds ?? BYO_PERF_DEFAULTS.timeout,
    retries: rc.max_item_requeues ?? BYO_PERF_DEFAULTS.retries,
    concurrency: rc.max_concurrency ?? BYO_PERF_DEFAULTS.concurrency,
  };
}
